// src/views/Login.jsx
import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import Logo from "../assets/logo.png";
import { toast } from "react-toastify";
import { Checkbox } from "antd";
import "react-toastify/dist/ReactToastify.css";
import { loginWithSupabase } from "../services/authService";
import useAuth from "../hooks/useAuth";
import { AuthSessionContext } from "../App";

import { getRolByNombre } from "../services/rolesService";
import { pickFirstAllowedRoute, NAV_CATALOG } from "../constants/nav";
import ModalSendEmail from "../modals/ModalSendEmail";

function normalizeReadPerms(permisosRaw) {
  const out = {};
  if (!permisosRaw || typeof permisosRaw !== "object") return out;

  for (const key of Object.keys(permisosRaw)) {
    const v = permisosRaw[key];
    if (v && typeof v === "object" && "read" in v) {
      out[key] = !!v.read;
    } else {
      out[key] = !!v;
    }
  }
  return out;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpenSendEmail, setIsModalOpenSendEmail] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const location = useLocation();
  const { refreshFromStorage } = useAuth();
  const { refreshFromStorage: refreshSessionContext } =
    useContext(AuthSessionContext);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.warn("Por favor ingresa tu correo y contraseña.");
      return;
    }

    if (!acceptedTerms) {
      toast.warn("Debes aceptar los términos y condiciones para continuar.");
      return;
    }

    setIsLoading(true);
    try {
      const { user } = await loginWithSupabase({ email, password });

      toast.success(`Bienvenido ${user?.user_metadata?.nombres || ""}!`);

      // Actualizar contextos
      refreshFromStorage();
      refreshSessionContext();

      // Calcular ruta destino según permisos del rol
      const rolRaw = user?.user_metadata?.rol || user?.role || "";
      const rolNorm = (rolRaw ?? "").toString(); // sin normalizar, tal cual

      let target = null;

      try {
        if (rolNorm) {
          const data = await getRolByNombre(rolNorm);
          const readPerms = normalizeReadPerms(data?.permisos);

          // 1) si venía de una ruta protegida y la tiene permitida, respétala
          const from = location.state?.from?.pathname;
          if (from) {
            const entry = Object.entries(NAV_CATALOG).find(([, def]) =>
              from.startsWith(def.to)
            );
            const currentModule = entry?.[0];
            if (currentModule && readPerms[currentModule]) {
              target = from;
            }
          }

          // 2) si no hay "from" válido, usa el primer módulo permitido
          if (!target) target = pickFirstAllowedRoute(readPerms);
        }
      } catch (e) {
        // si falla la lectura de permisos, continua con fallback
      }

      // 3) Fallback: sin permisos -> pantalla "sin acceso"
      if (!target) {
        target = "/futbolito/sin-acceso";
      }

      navigate(target, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "No se pudo iniciar sesión");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = () => setIsModalOpenSendEmail(true);

  const handleModalCancel = () => setIsModalOpenSendEmail(false);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-4xl border border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">
          BIENVENIDOS
        </h1>

        <div className="flex flex-col lg:flex-row">
          {/* Logo */}
          <div className="lg:w-1/2 flex flex-col justify-center items-center lg:border-r lg:border-gray-200">
            <img
              src={Logo}
              alt="Logo principal"
              className="h-60 object-contain mb-4 lg:mb-0"
            />
          </div>

          {/* Formulario */}
          <div className="lg:w-1/2 p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  className="block text-gray-700 text-sm font-medium mb-2"
                  htmlFor="username"
                >
                  Correo electrónico
                </label>
                <input
                  id="username"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="example@gmail.com"
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="relative">
                <label
                  className="block text-gray-700 text-sm font-medium mb-2"
                  htmlFor="password"
                >
                  Contraseña
                </label>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Ingresa tu contraseña"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 bottom-3 text-gray-500 hover:text-blue-600 transition disabled:opacity-50"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeInvisibleOutlined className="text-lg" />
                  ) : (
                    <EyeOutlined className="text-lg" />
                  )}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#00376d] hover:bg-[#0051a1] text-white font-medium py-3 px-4 rounded-lg transition duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
              </button>

              <div className="mt-4">
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={isLoading}
                >
                  <span className="text-sm text-gray-700">
                    Acepto los{" "}
                    <a
                      href="https://1drv.ms/b/c/146bc63256e0cf69/IQCEU9OIlqsvRaVVDqVYDE8oAZkRwgXdV09NW0rdpeJBGhI?e=lzfwSV"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00376d] hover:text-blue-800 hover:underline font-medium"
                    >
                      términos y condiciones
                    </a>
                  </span>
                </Checkbox>
              </div>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-sm text-[#00376d] hover:text-blue-800 hover:underline bg-transparent border-none p-0 cursor-pointer"
                onClick={handleForgotPasswordClick}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalSendEmail
        open={isModalOpenSendEmail}
        onCancel={handleModalCancel}
      />
    </div>
  );
};

export default Login;
